import store from '../../store';
import { PriceCalc } from "./priceCalc";

const DATA_LAYER_NAME = 'dataLayer';

// GTM_ID は Vuex から優先取得し、未設定時は既定値を使用する。
function getGtmId() {
  const gtmIdFromGetter = store && store.getters ? store.getters.gtmId : '';
  if (gtmIdFromGetter) {
    return gtmIdFromGetter;
  }

  const gtmIdFromState = store && store.state ? store.state.gtmId : '';
  if (gtmIdFromState) {
    return gtmIdFromState;
  }

  return '';
}

// グローバル共有の GTM dataLayer を返し、初回利用時は自動で初期化する。
function getDataLayer() {
  if (!window[DATA_LAYER_NAME]) {
    window[DATA_LAYER_NAME] = [];
  }
  return window[DATA_LAYER_NAME];
}

// ログ出力
function consoleLogGtm(event, DATA_LAYER) {
  console.log("consoleLogGtm event:DATA_LAYER", event, DATA_LAYER);
}

// noscript 環境との互換のため、noscript iframe は一度だけ追加して重複挿入を防ぐ。
function appendNoScriptIframe(gtmId) {
  if (document.querySelector(`iframe[src="https://www.googletagmanager.com/ns.html?id=${gtmId}"]`)) {
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);
}

// GTM スクリプトは一度だけ挿入し、コンテナの重複読み込みを防ぐ。
function appendScriptTag(gtmId) {
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) {
    return;
  }

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

// 任意の EC イベントを push する前に、GTM 起動に必要なタグが準備済みであることを保証する。
export function ensureGtmLoaded() {
  const gtmId = getGtmId();
  if (!gtmId) return
  const dataLayer = getDataLayer();
  const hasStartEvent = dataLayer.some(entry => entry && entry['gtm.start']);

  if (!hasStartEvent) {
    dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  appendScriptTag(gtmId);
  appendNoScriptIframe(gtmId);
}

// PT120 / PT125 の商品データから通常購入・定期購入の価格を算出する。
export function resolvePrice(productInfo = {}, teiki) {
  if ((productInfo.POINT_ITEM_KBN == '0' || productInfo.POINT_ITEM_KBN == '2') && (productInfo.NEBIKI_PRICE > 0)) {
    // 値引き価格あり
    const ITEM_ZEI_SET_KBN = productInfo.ITEM_ZEI_SET_KBN || 0; // 消費税率
    const NEBIKI_PRICE = productInfo.NEBIKI_PRICE; // 値引き価格（税込）
    const SALE_TNK = PriceCalc.div(NEBIKI_PRICE,
      PriceCalc.div(PriceCalc.add(100, ITEM_ZEI_SET_KBN)
        , 100) // 値引き価格（税抜）
    );
    const value = teiki ? productInfo.TEIKI_TNK : SALE_TNK; // 税抜き値引き価格
    return value || 0;
  }
  const value = teiki ? productInfo.TEIKI_TNK : productInfo.SALE_TNK; // 税抜き価格
  return value || 0;
}

// PT120 商品を GA4 item 形式へマッピングする。
export function buildPtDetailItem(productInfo = {}, navList = [], teiki = false) {
  return {
    item_id: productInfo.S_ITEM_NO, // 商品ID
    item_name: productInfo.WEB_ITEM_NM, // 商品名
    ...(navList?.[0]?.[0]?.CATEGORY_NM ? { item_category: navList[0][0].CATEGORY_NM } : {}), // 第1カテゴリ
    ...(navList?.[0]?.[1]?.CATEGORY_NM ? { item_category2: navList[0][1].CATEGORY_NM } : {}), // 第2カテゴリ
    ...(navList?.[0]?.[2]?.CATEGORY_NM ? { item_category3: navList[0][2].CATEGORY_NM } : {}), // 第3カテゴリ
    ...(navList?.[0]?.[3]?.CATEGORY_NM ? { item_category4: navList[0][3].CATEGORY_NM } : {}), // 第4カテゴリ
    ...(navList?.[0]?.[4]?.CATEGORY_NM ? { item_category5: navList[0][4].CATEGORY_NM } : {}), // 第5カテゴリ
    price: resolvePrice(productInfo, teiki), // 税抜き値引き価格
    discount: PriceCalc.sub(productInfo.SALE_TNK, resolvePrice(productInfo, teiki)), // 値引き額
    ...(document.referrer && enrichItem(productInfo.S_ITEM_NO) ? enrichItem(productInfo.S_ITEM_NO) : {})
  };
}

// SH070 の注文商品を GA4 item 形式へマッピングする。
export function buildPurchaseItem(item) {
  const quantity = Number(item.QTY);
  const price = Number(item.TNK_TAX_EX); // 税抜き値引き価格

  return {
    item_id: item.S_ITEM_CD,
    item_name: item.ITEM_NM,
    quantity: Number.isNaN(quantity) || quantity <= 0 ? 1 : quantity,
    price: Number.isNaN(price) ? 0 : price,
    ...( enrichItem(item.S_ITEM_CD) ? enrichItem(item.S_ITEM_CD) : {})
  };
}

// EC イベントを送信する
export function pushEcommerceEvent(eventName, ecommerce = {}) {
  consoleLogGtm(eventName, ecommerce);
  // return
  const dataLayer = getDataLayer();
  // 次の GTM EC イベントを push する前に、前回の ecommerce 状態をクリアする。
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: eventName,
    ecommerce,
  });
}


const STORAGE_KEY = "ga4_item_source_map";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;

export function readMap() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (e) {
    return {};
  }
}

export function writeMap(map) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}
export function enrichItem(id) {
  const source = getItemSource(id);

  return {
    item_list_id: source?.item_list_id,
    item_list_name: source?.item_list_name,
    index: source?.index
  };
}
export function saveItemSource({
  item_id,
  item_list_id,
  item_list_name,
  index,
  source_event
}) {
  if (!item_id) return;

  const map = readMap();

  const existing = map[item_id];

  const priority = {
    view_item_list: 1,
    select_item: 2,
    view_item: 3,
  };

  const newPriority = priority[source_event] || 0;
  const oldPriority = priority[existing?.source_event] || 0;

  // 优先保留更强信号，或者同级时用最新时间覆盖
  if (!existing || newPriority >= oldPriority) {
    map[item_id] = {
      item_list_id: item_list_id || "",
      item_list_name: item_list_name || "",
      index,
      source_event: source_event || "unknown",
      updated_at: Date.now()
    };
    writeMap(map);
  }
}

export function getItemSource(item_id) {
  const map = readMap();
  const source = map[item_id];

  if (!source) return null;

  if (Date.now() - source.updated_at > MAX_AGE) {
    delete map[item_id];
    writeMap(map);
    return null;
  }

  return source;
}
